/**
 * 统一的富文本编辑器 - 基于 Tiptap
 * 支持 Markdown 输入，富文本编辑和预览
 */
import React, { useEffect, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import TextAlign from '@tiptap/extension-text-align';
import TurndownService from 'turndown';
import { marked } from 'marked';
import EditorContextMenu from './EditorContextMenu';
import ImageDialog from './ImageDialog';
import MermaidDialog from './MermaidDialog';
import TableDialog from './TableDialog';
import ParagraphOptimizeDialog from './ParagraphOptimizeDialog';
import { MermaidNode } from './MermaidExtension';
import { PageBreakNode } from './PageBreakExtension';

// 抑制 Tiptap 的重复扩展警告（React StrictMode 导致的双重渲染）
const originalWarn = console.warn;
console.warn = (...args: any[]) => {
  const message = args[0];
  if (
    typeof message === 'string' && 
    message.includes('[tiptap warn]') && 
    message.includes('Duplicate extension names')
  ) {
    return; // 忽略此警告
  }
  originalWarn.apply(console, args);
};

// 恢复 console.warn（在组件卸载时）
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    console.warn = originalWarn;
  });
}

// 初始化 turndown 服务（全局单例，避免重复创建）
const turndownService = new TurndownService({
  headingStyle: 'atx',
  codeBlockStyle: 'fenced',
  br: '  ',  // 将 <br> 转为两个空格 + 换行（Markdown 硬换行）
});
turndownService.escape = (text: string) => text;

// 添加图片规则
turndownService.addRule('image', {
  filter: 'img',
  replacement: (content: string, node: any) => {
    const alt = node.alt || '';
    const src = node.getAttribute('src') || '';
    const title = node.title ? ` "${node.title}"` : '';
    return src ? `![${alt}](${src}${title})` : '';
  }
});

// 添加 <br> 标签规则 - 保留换行
turndownService.addRule('lineBreak', {
  filter: 'br',
  replacement: () => '  \n'  // Markdown 硬换行：两个空格 + 换行
});

// 添加文本对齐规则
turndownService.addRule('textAlign', {
  filter: (node: any) => {
    if (!node.style || !node.style.textAlign) return false;
    const align = node.style.textAlign;
    return (align === 'center' || align === 'right' || align === 'left') && 
           (node.nodeName === 'P' || node.nodeName === 'H1' || node.nodeName === 'H2' || 
            node.nodeName === 'H3' || node.nodeName === 'H4' || node.nodeName === 'H5' || 
            node.nodeName === 'H6');
  },
  replacement: (content: string, node: any) => {
    const align = node.style.textAlign;
    if (!content.trim()) return '';
    
    // 使用 HTML 注释标记保留对齐信息
    if (align && align !== 'left') {
      return `<!-- align:${align} -->\n${content}\n<!-- /align -->\n\n`;
    }
    
    return content + '\n\n';
  }
});

// 添加注记标签规则：保留注记标记
turndownService.addRule('annotation', {
  filter: (node: any) => {
    return node.nodeName === 'SPAN' && 
           node.classList && node.classList.contains('annotation-mark') && 
           node.hasAttribute && node.hasAttribute('data-annotation-id');
  },
  replacement: (content, node: any) => {
    const annotationId = node.getAttribute('data-annotation-id');
    // 保留原始 HTML 标签
    return `<span class="annotation-mark" data-annotation-id="${annotationId}">${content}</span>`;
  }
});

// 添加表格规则 - 保留 HTML 表格（大模型生成的表格）
turndownService.addRule('table', {
  filter: 'table',
  replacement: (content: string, node: any) => {
    return '\n' + node.outerHTML + '\n'; // 保留完整的 HTML 表格
  }
});

// 添加分页符规则：转换为特殊标记
turndownService.addRule('pageBreak', {
  filter: (node: any) => {
    // 更精确的匹配：检查是否是div元素且有page-break属性
    return node.nodeName === 'DIV' && node.getAttribute('data-type') === 'page-break';
  },
  replacement: () => {
    return '\n<!-- PAGE_BREAK -->\n';
  }
});
turndownService.addRule('mermaid', {
  filter: (node: any) => {
    return node.getAttribute('data-type') === 'mermaid';
  },
  replacement: (content: string, node: any) => {
    // 从节点中提取 Mermaid 代码
    const codeElement = node.querySelector('[data-mermaid-code]');
    const code = codeElement?.getAttribute('data-mermaid-code') || '';
    return `\n\`\`\`mermaid\n${code}\n\`\`\`\n`;
  }
});

// JSON 转 Markdown 函数（处理 Tiptap JSON 格式）
const jsonToMarkdown = (doc: any): string => {
  if (!doc || !doc.content) return '';
  
  const processNode = (node: any): string => {
    const type = node.type;
    
    // 分页符节点
    if (type === 'pageBreak') {
      return '\n<!-- PAGE_BREAK -->\n\n';
    }
    
    // Mermaid 节点
    if (type === 'mermaid') {
      const code = node.attrs?.code || '';
      return `\n\`\`\`mermaid\n${code}\n\`\`\`\n\n`;
    }
    
    // 段落
    if (type === 'paragraph') {
      const align = node.attrs?.textAlign;
      let text = processContent(node.content);
      if (align && align !== 'left') {
        return `<!-- align:${align} -->\n${text}\n<!-- /align -->\n\n`;
      }
      return text + '\n\n';
    }
    
    // 标题
    if (type === 'heading') {
      const level = node.attrs?.level || 1;
      const text = processContent(node.content);
      const align = node.attrs?.textAlign;
      const heading = '#'.repeat(level) + ' ' + text;
      if (align && align !== 'left') {
        return `<!-- align:${align} -->\n${heading}\n<!-- /align -->\n\n`;
      }
      return heading + '\n\n';
    }
    
    // 代码块
    if (type === 'codeBlock') {
      const code = processContent(node.content);
      const language = node.attrs?.language || '';
      return `\n\`\`\`${language}\n${code}\n\`\`\`\n\n`;
    }
    
    // 列表
    if (type === 'bulletList') {
      return processContent(node.content, '- ');
    }
    
    if (type === 'orderedList') {
      return processContent(node.content, (index: number) => `${index + 1}. `);
    }
    
    if (type === 'listItem') {
      return processContent(node.content);
    }
    
    // 引用
    if (type === 'blockquote') {
      const text = processContent(node.content);
      return text.split('\n').map(line => '> ' + line).join('\n') + '\n\n';
    }
    
    // 硬换行
    if (type === 'hardBreak') {
      return '  \n';
    }
    
    // 表格节点处理（使用 HTML 保留完整结构）
    if (type === 'table') {
      return '\n<table>\n' + processContent(node.content) + '</table>\n\n';
    }
    
    if (type === 'tableRow') {
      return '<tr>\n' + processContent(node.content) + '</tr>\n';
    }
    
    if (type === 'tableCell') {
      const content = processContent(node.content) || '';
      return '<td>' + content + '</td>\n';
    }
    
    if (type === 'tableHeader') {
      const content = processContent(node.content) || '';
      const style = node.attrs?.style || '';
      if (style) {
        return `<th style="${style}">${content}</th>\n`;
      }
      return '<th>' + content + '</th>\n';
    }
    
    // 图片
    if (type === 'image') {
      const src = node.attrs?.src || '';
      const alt = node.attrs?.alt || '';
      const title = node.attrs?.title || '';
      return title ? `![${alt}](${src} "${title}")` : `![${alt}](${src})`;
    }
    
    // 文本
    if (type === 'text') {
      let text = node.text || '';
      
      // 处理文本标记
      if (node.marks) {
        node.marks.forEach((mark: any) => {
          if (mark.type === 'bold') text = `**${text}**`;
          if (mark.type === 'italic') text = `*${text}*`;
          if (mark.type === 'code') text = `\`${text}\``;
          if (mark.type === 'link') {
            const href = mark.attrs?.href || '';
            text = `[${text}](${href})`;
          }
        });
      }
      
      return text;
    }
    
    // 其他节点，递归处理内容
    return processContent(node.content);
  };
  
  const processContent = (content: any[], prefix?: string | ((index: number) => string)): string => {
    if (!content) return '';
    
    return content.map((node, index) => {
      const result = processNode(node);
      if (prefix) {
        if (typeof prefix === 'function') {
          return prefix(index) + result;
        }
        return prefix + result;
      }
      return result;
    }).join('');
  };
  
  return processContent(doc.content);
};

// 解析对齐注释并转换为样式（全局函数）
const parseContentWithAlignment = (markdown: string): string => {
  // 先处理 Mermaid 代码块，转换为自定义节点
  let processedMarkdown = markdown.replace(/```mermaid\n([\s\S]*?)\n```/g, (match, code) => {
    const trimmedCode = code.trim();
    // 返回特殊的 HTML 标记，稍后会被 Tiptap 识别
    return `<div data-type="mermaid" data-mermaid-code="${encodeURIComponent(trimmedCode)}"></div>`;
  });
  
  // 处理分页符标记：先转换为临时占位符，避免被marked当作HTML注释过滤
  const pageBreakPlaceholder = '{{PAGEBREAK_PLACEHOLDER}}';
  processedMarkdown = processedMarkdown.replace(/<!-- PAGE_BREAK -->/g, pageBreakPlaceholder);
  if ((processedMarkdown.match(/\{\{PAGEBREAK_PLACEHOLDER\}\}/g) || []).length > 0) {
    console.log(`📑 检测到 ${(processedMarkdown.match(/\{\{PAGEBREAK_PLACEHOLDER\}\}/g) || []).length} 个分页符标记`);
  }
    
  // 然后将 Markdown 转为 HTML
  let result = marked(processedMarkdown, { 
    breaks: true,
    gfm: true,
  }) as string;
  
  // marked处理后，将占位符替换为完整的分页符HTML
  result = result.replace(/\{\{PAGEBREAK_PLACEHOLDER\}\}/g, 
    '<div data-type="page-break" class="page-break-node"><div class="page-break-line"><span class="page-break-text">分页符</span></div></div>'
  );
    
  // 然后处理对齐注释
  const pattern = /<!-- align:(center|right|left) -->\s*([\s\S]*?)\s*<!-- \/align -->/g;
  
  result = result.replace(pattern, (match, align, content) => {
    const trimmedContent = content.trim();
    
    // 检查是否是标题 HTML（<h1>-<h6>）
    const headingMatch = trimmedContent.match(/^<(h[1-6])>(.*)<\/h[1-6]>$/);
    if (headingMatch) {
      const tag = headingMatch[1];
      const text = headingMatch[2];
      return `<${tag} style="text-align: ${align}">${text}</${tag}>\n`;
    }
    
    // 检查是否是段落 HTML（<p>...</p>）
    const paraMatch = trimmedContent.match(/^<p>(.*)<\/p>$/);
    if (paraMatch) {
      const text = paraMatch[1];
      return `<p style="text-align: ${align}">${text}</p>\n`;
    }
    
    // 其他情况，用 div 包裹
    return `<div style="text-align: ${align}">${trimmedContent}</div>\n`;
  });
  
  return result;
};

interface UnifiedEditorProps {
  content: string;           // Markdown 或 HTML
  editable?: boolean;        // 是否可编辑
  onChange?: (markdown: string) => void;
  onExit?: () => void;
  className?: string;
  selectedModel?: string;    // 项目配置的模型（可选，用于段落优化）
  businessLicenseUrl?: string; // 新增：公司营业执照URL
}

const UnifiedEditor: React.FC<UnifiedEditorProps> = ({ 
  content, 
  editable = true, 
  onChange, 
  onExit,
  className = '',
  selectedModel = '',  // 默认为空字符串
  businessLicenseUrl, // 新增
}) => {
  const [showImageDialog, setShowImageDialog] = useState(false);
  const [selectedTextForImage, setSelectedTextForImage] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [lastContent, setLastContent] = useState(content); // 记录上次的内容
  
  // 右键菜单状态
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 });
  const [cursorPosition, setCursorPosition] = useState<number | null>(null);
  const [isInTable, setIsInTable] = useState(false);
  const [hasSelectedText, setHasSelectedText] = useState(false); // 是否有选中文字
  const [isInPageBreak, setIsInPageBreak] = useState(false); // 是否在分页符上
  
  // Mermaid 对话框状态
  const [showMermaidDialog, setShowMermaidDialog] = useState(false);
  const [contextText, setContextText] = useState('');
  
  // Table 对话框状态
  const [showTableDialog, setShowTableDialog] = useState(false);

  // 段落优化对话框状态
  const [showParagraphOptimizeDialog, setShowParagraphOptimizeDialog] = useState(false);
  const [selectedTextForOptimize, setSelectedTextForOptimize] = useState('');

  // 抑制 Tiptap 的 link 重复警告（开发模式下正常）
  useEffect(() => {
    const originalWarn = console.warn;
    console.warn = (...args: any[]) => {
      if (args[0]?.includes?.('Duplicate extension names')) {
        return; // 忽略这个警告
      }
      originalWarn(...args);
    };
    return () => {
      console.warn = originalWarn;
    };
  }, []);
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3, 4, 5, 6],
        },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-blue-600 underline hover:text-blue-800',
        },
      }),
      Image.configure({
        HTMLAttributes: {
          class: 'max-w-full h-auto rounded-lg my-4',
        },
      }).extend({
        // 扩展Image节点，在渲染时动态处理相对路径
        addNodeView() {
          return ({ node }) => {
            const dom = document.createElement('img');
            let src = node.attrs.src;
            
            // 如果是相对路径且以/uploads/开头，在开发环境拼接API_BASE_URL
            // 生产环境REACT_APP_API_URL为空，直接使用相对路径即可（前后端同端口，浏览器自动解析）
            if (src && src.startsWith('/uploads/')) {
              const API_BASE_URL = process.env.REACT_APP_API_URL;
              if (API_BASE_URL) {
                src = `${API_BASE_URL}${src}`;
              }
            }
            
            dom.src = src;
            dom.className = 'max-w-full h-auto rounded-lg my-4';
            if (node.attrs.alt) dom.alt = node.attrs.alt;
            if (node.attrs.title) dom.title = node.attrs.title;
            
            return {
              dom,
              update: (updatedNode) => {
                if (updatedNode.type.name !== 'image') return false;
                let newSrc = updatedNode.attrs.src;
                if (newSrc && newSrc.startsWith('/uploads/')) {
                  const API_BASE_URL = process.env.REACT_APP_API_URL;
                  if (API_BASE_URL) {
                    newSrc = `${API_BASE_URL}${newSrc}`;
                  }
                }
                dom.src = newSrc;
                if (updatedNode.attrs.alt) dom.alt = updatedNode.attrs.alt;
                if (updatedNode.attrs.title) dom.title = updatedNode.attrs.title;
                return true;
              },
            };
          };
        },
      }),
      Table.configure({
        resizable: false,  // 禁用调整大小
        HTMLAttributes: {
          class: 'border-collapse border border-gray-300 my-4 relative z-10',
        },
      }),
      TableRow,
      TableCell.configure({
        HTMLAttributes: {
          class: 'border border-gray-300 px-4 py-2',
        },
      }).extend({
        addAttributes() {
          return {
            ...this.parent?.(),
            style: {
              default: null,
              parseHTML: element => element.getAttribute('style'),
              renderHTML: attributes => {
                if (!attributes.style) {
                  return {};
                }
                return { style: attributes.style };
              },
            },
          };
        },
      }),
      TableHeader.configure({
        HTMLAttributes: {
          class: 'border border-gray-300 px-4 py-2 font-semibold',
        },
      }).extend({
        addAttributes() {
          return {
            ...this.parent?.(),
            style: {
              default: null,
              parseHTML: element => element.getAttribute('style'),
              renderHTML: attributes => {
                if (!attributes.style) {
                  return {};
                }
                return { style: attributes.style };
              },
            },
          };
        },
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
        alignments: ['left', 'center', 'right', 'justify'],
        defaultAlignment: 'left',
      }),
      MermaidNode, // 添加 Mermaid 节点
      PageBreakNode, // 添加分页符节点
    ],
    content: parseContentWithAlignment(content),
    editable: editable,
    editorProps: {
      attributes: {
        class: 'focus:outline-none',
      },
      // 处理右键菜单
      handleDOMEvents: {
        contextmenu: (view, event) => {
          if (!editable) return false;
          
          event.preventDefault();
          
          const { state } = view;
          const { from, to, empty } = state.selection;
          
          // 检测是否在表格内或分页符上
          const $pos = state.doc.resolve(from);
          let inTable = false;
          let inPageBreak = false;
          
          console.log('🔍 右键检测 - 光标位置:', from, '深度:', $pos.depth);
          
          for (let d = $pos.depth; d > 0; d--) {
            const node = $pos.node(d);
            console.log(`  层级 ${d}: 节点类型=${node.type.name}`);
            
            if (node.type.name === 'table') {
              inTable = true;
            }
            if (node.type.name === 'pageBreak') {
              inPageBreak = true;
              console.log('  ✅ 检测到分页符节点！');
            }
          }
          
          // 如果没有在深度中找到，检查当前位置的节点
          const currentNode = state.doc.nodeAt(from);
          if (currentNode && currentNode.type.name === 'pageBreak') {
            inPageBreak = true;
            console.log('  ✅ 当前位置就是分页符节点！');
          }
          
          console.log('📊 检测结果: inTable=', inTable, 'inPageBreak=', inPageBreak);
          
          setIsInTable(inTable);
          setIsInPageBreak(inPageBreak);
          
          let extractedText = '';
          let hasSelection = false;
          
          if (!empty) {
            // 有选中内容，提取选中的文字
            extractedText = state.doc.textBetween(from, to, '\n\n');
            hasSelection = extractedText.trim().length > 0;
            // 记录光标位置为选中区域的结束位置（插入到选中文字后面）
            setCursorPosition(to);
          } else {
            // 没有选中内容，不提取任何文字，传递空字符串
            extractedText = '';
            hasSelection = false;
            // 记录当前光标位置
            setCursorPosition(from);
          }
          
          setContextText(extractedText);
          setHasSelectedText(hasSelection);
          
          // 显示菜单
          setContextMenuPosition({
            x: event.clientX,
            y: event.clientY,
          });
          setShowContextMenu(true);
          
          return true;
        },
      },
    },
    onUpdate: ({ editor }) => {
      if (onChange && editable) {
        // 获取 JSON 格式的编辑器内容
        const json = editor.getJSON();
        
        // 手动将 JSON 转换为 Markdown
        const markdown = jsonToMarkdown(json);
        
        onChange(markdown);
      }
    },
  }, []);

  // 监听 content 变化并更新编辑器
  useEffect(() => {
    if (!editor) return;
    
    // 如果是编辑模式且编辑器有焦点，不更新（避免光标跳动）
    if (editable && editor.isFocused) {
      return;
    }
    
    // 如果内容有变化，更新编辑器
    if (content !== lastContent) {
      const newContent = parseContentWithAlignment(content);
      editor.commands.setContent(newContent);
      setLastContent(content);
    }
  }, [editor, content, lastContent, editable]);

  // 点击编辑器外部时退出编辑
  useEffect(() => {
    if (!editable || !onExit) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.unified-editor') && !showImageDialog) {
        onExit();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [editable, onExit, showImageDialog]);

  // 插入图片
  const handleInsertImage = (urls: string[]) => {
    if (urls.length > 0 && editor) {
      const { from } = editor.state.selection;
      
      // 构建要插入的内容：多张图片间用段落分隔
      const content = urls.map((url, index) => {
        if (index < urls.length - 1) {
          return `<img src="${url}" /><p></p>`;
        } else {
          return `<img src="${url}" />`;
        }
      }).join('');
      
      // 先插入内容，再 focus 到插入位置后面
      editor.chain()
        .insertContentAt(from, content)
        .focus(from + 1)
        .run();
      
      setShowImageDialog(false);
    }
  };

  // 打开插入图片对话框
  const openImageDialog = () => {
    const { from, to } = editor?.state.selection || {};
    const text = editor?.state.doc.textBetween(from || 0, to || 0, ' ') || '';
    setSelectedTextForImage(text);
    setShowImageDialog(true);
  };

  // 插入 Mermaid 流程图
  const handleInsertMermaid = (code: string) => {
    if (!editor || cursorPosition === null) return;
    
    // 不使用 .focus() 避免滚动跳转
    editor.chain()
      .setTextSelection(cursorPosition)
      .insertContent({
        type: 'mermaid',
        attrs: {
          code: code,
        },
      })
      .run();
  };
  
  // 插入表格
  const handleInsertTable = (tableData: { rows: number; cols: number; data: string[][]; headerColor?: string; textColor?: string }) => {
    if (!editor) return;
    
    console.log('🔷 开始插入表格:', tableData);
    
    try {
      // 生成完整的 HTML 表格（包含内容和样式）
      const headerColor = tableData.headerColor || '#3B82F6';
      const textColor = tableData.textColor || '#FFFFFF'; // 默认白色文字
      let tableHTML = '<table class="border-collapse border border-gray-300 my-4 relative z-10">\n';
      
      for (let r = 0; r < tableData.rows; r++) {
        tableHTML += '<tr>\n';
        
        for (let c = 0; c < tableData.cols; c++) {
          const content = tableData.data[r]?.[c] || '';
          // HTML 转义
          const escapedContent = content
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');
          
          // 空内容需要包裹 <p> 标签，否则 ProseMirror tableCell 会报错
          const cellContent = escapedContent || '<p></p>';
          
          if (r === 0) {
            // 第一行是表头 - 使用背景色和文字颜色（添加 !important 确保生效）
            const headerStyle = `background: ${headerColor} !important; background-color: ${headerColor} !important; color: ${textColor} !important;`;
            console.log(`  🎨 表头样式: ${headerStyle}`);
            tableHTML += `<th class="border border-gray-300 px-4 py-2 font-semibold" style="${headerStyle}">${cellContent}</th>\n`;
          } else {
            // 其他行是数据单元格
            tableHTML += `<td class="border border-gray-300 px-4 py-2">${cellContent}</td>\n`;
          }
        }
        
        tableHTML += '</tr>\n';
      }
      
      tableHTML += '</table>';
      
      console.log('📝 生成的表格 HTML（前500字符）:', tableHTML.substring(0, 500));
      
      // 插入表格（不带 focus，避免跳转）
      editor.commands.insertContent(tableHTML);
      
      console.log('✅ 表格插入完成');
      
    } catch (err) {
      console.error('❌ 插入表格异常:', err);
      alert('插入表格出错: ' + (err as Error).message);
    }
  };
  
  // 打开 Mermaid 对话框
  const handleOpenMermaidDialog = () => {
    setShowMermaidDialog(true);
  };
  
  // 打开表格对话框
  const handleOpenTableDialog = () => {
    if (!editor) {
      setShowTableDialog(true);
      return;
    }
    
    // 获取选中的文本
    const { from, to } = editor.state.selection;
    const selectedText = editor.state.doc.textBetween(from, to, '\n');
    
    if (selectedText.trim()) {
      setContextText(selectedText);
    } else {
      setContextText('');
    }
    
    setShowTableDialog(true);
  };
  
  // 打开段落优化对话框
  const handleOpenParagraphOptimize = () => {
    if (!editor) return;
    
    const { from, to } = editor.state.selection;
    const selectedText = editor.state.doc.textBetween(from, to, '\n');
    
    if (selectedText.trim()) {
      setSelectedTextForOptimize(selectedText);
      setShowParagraphOptimizeDialog(true);
    }
  };
  
  // 替换选中的文字
  const handleReplaceParagraph = (newText: string) => {
    if (!editor) return;
    
    const { from, to } = editor.state.selection;
    
    // 使用 commands 而不是 chain().focus()，避免光标跳转
    editor.commands.deleteRange({ from, to });
    editor.commands.insertContent(newText);
    
    console.log('✅ 段落已替换');
  };
  
  // 插入分页符
  const handleInsertPageBreak = () => {
    if (!editor) return;
    
    editor.chain()
      .focus()
      .setPageBreak()
      .run();
    
    console.log('✅ 分页符已插入');
  };
  
  // 删除分页符
  const handleDeletePageBreak = () => {
    if (!editor) return;
    
    try {
      // 获取当前选区
      const { from } = editor.state.selection;
      const $pos = editor.state.doc.resolve(from);
      
      console.log('🗑️ 尝试删除分页符，光标位置:', from, '深度:', $pos.depth);
      
      // 首先检查当前位置的节点
      const currentNode = editor.state.doc.nodeAt(from);
      if (currentNode && currentNode.type.name === 'pageBreak') {
        const nodeSize = currentNode.nodeSize;
        console.log('  找到分页符节点（当前位置），大小:', nodeSize);
        
        // 删除当前位置的节点
        editor.chain()
          .deleteRange({ from: from, to: from + nodeSize })
          .run();
        
        console.log('✅ 分页符已删除（方式1：当前位置）');
        return;
      }
      
      // 如果当前位置不是，查找父节点
      for (let d = $pos.depth; d > 0; d--) {
        const node = $pos.node(d);
        console.log(`  检查层级 ${d}: 节点类型=${node.type.name}`);
        
        if (node.type.name === 'pageBreak') {
          // 计算节点的位置
          const pos = $pos.before(d);
          const nodeSize = node.nodeSize;
          console.log('  找到分页符节点（父级），位置:', pos, '大小:', nodeSize);
          
          // 删除节点
          editor.chain()
            .deleteRange({ from: pos, to: pos + nodeSize })
            .run();
          
          console.log('✅ 分页符已删除（方式2：父节点）');
          return;
        }
      }
      
      console.warn('⚠️ 未找到分页符节点');
    } catch (err) {
      console.error('❌ 删除分页符失败:', err);
    }
  };
  
  // 删除表格
  const handleDeleteTable = () => {
    if (!editor) return;
    
    try {
      // 不使用 focus，避免跳转
      editor.commands.deleteTable();
      console.log('✅ 表格已删除');
    } catch (err) {
      console.error('❌ 删除表格失败:', err);
      alert('删除表格失败');
    }
  };
  
  // 打开图表对话框（暂时使用 Mermaid）
  const handleOpenChartDialog = () => {
    setContextText(''); // 清空上下文，使用默认模板
    setShowMermaidDialog(true);
  };

  // 阻止工具栏按钮导致的滚动跳动
  const preventScrollOnClick = (e: React.MouseEvent) => {
    e.preventDefault();
  };

  if (!editor) {
    return null;
  }

  return (
    <div className={`unified-editor ${isFullscreen ? 'fixed inset-0 z-40 bg-white flex flex-col' : ''} ${className}`}>
      {/* 工具栏 - 仅编辑模式显示 */}
      {editable && (
        <div className="sticky top-0 bg-white border-b border-gray-200 shadow-sm z-10">
          <div className="px-4 py-2 flex items-center justify-between flex-wrap gap-2">
            {/* 左侧：格式工具 */}
            <div className="flex items-center gap-2 flex-wrap">
              {/* 文本样式组 */}
              <div className="flex items-center gap-0.5 px-1.5 py-1 bg-gray-50 rounded-md border border-gray-200">
                <button
                  onMouseDown={preventScrollOnClick}
                  onClick={() => editor.chain().focus().toggleBold().run()}
                  disabled={!editor.can().chain().focus().toggleBold().run()}
                  className={`p-1.5 rounded transition-all ${
                    editor.isActive('bold') 
                      ? 'bg-blue-500 text-white shadow-sm' 
                      : 'hover:bg-gray-200 text-gray-700'
                  } disabled:opacity-30 disabled:cursor-not-allowed`}
                  title="加粗 (Ctrl+B)"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M15.6 10.79c.97-.67 1.65-1.77 1.65-2.79 0-2.26-1.75-4-4-4H7v14h7.04c2.09 0 3.71-1.7 3.71-3.79 0-1.52-.86-2.82-2.15-3.42zM10 6.5h3c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5h-3v-3zm3.5 9H10v-3h3.5c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5z"/>
                  </svg>
                </button>
              </div>

              <div className="w-px h-6 bg-gray-300" />

              {/* 标题组 */}
              <div className="flex items-center gap-0.5 px-1.5 py-1 bg-gray-50 rounded-md border border-gray-200">
                <button
                  onMouseDown={preventScrollOnClick}
                  onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                  className={`px-2 py-1.5 text-xs font-semibold rounded transition-all ${
                    editor.isActive('heading', { level: 1 }) 
                      ? 'bg-blue-500 text-white shadow-sm' 
                      : 'hover:bg-gray-200 text-gray-700'
                  }`}
                  title="一级标题"
                >
                  H1
                </button>
                <button
                  onMouseDown={preventScrollOnClick}
                  onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                  className={`px-2 py-1.5 text-xs font-semibold rounded transition-all ${
                    editor.isActive('heading', { level: 2 }) 
                      ? 'bg-blue-500 text-white shadow-sm' 
                      : 'hover:bg-gray-200 text-gray-700'
                  }`}
                  title="二级标题"
                >
                  H2
                </button>
                <button
                  onMouseDown={preventScrollOnClick}
                  onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                  className={`px-2 py-1.5 text-xs font-semibold rounded transition-all ${
                    editor.isActive('heading', { level: 3 }) 
                      ? 'bg-blue-500 text-white shadow-sm' 
                      : 'hover:bg-gray-200 text-gray-700'
                  }`}
                  title="三级标题"
                >
                  H3
                </button>
              </div>

              <div className="w-px h-6 bg-gray-300" />

              {/* 列表和引用组 */}
              <div className="flex items-center gap-0.5 px-1.5 py-1 bg-gray-50 rounded-md border border-gray-200">
                <button
                  onMouseDown={preventScrollOnClick}
                  onClick={() => editor.chain().focus().toggleBulletList().run()}
                  className={`p-1.5 rounded transition-all ${
                    editor.isActive('bulletList') 
                      ? 'bg-blue-500 text-white shadow-sm' 
                      : 'hover:bg-gray-200 text-gray-700'
                  }`}
                  title="无序列表"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16"/>
                  </svg>
                </button>
                <button
                  onMouseDown={preventScrollOnClick}
                  onClick={() => editor.chain().focus().toggleOrderedList().run()}
                  className={`p-1.5 rounded transition-all ${
                    editor.isActive('orderedList') 
                      ? 'bg-blue-500 text-white shadow-sm' 
                      : 'hover:bg-gray-200 text-gray-700'
                  }`}
                  title="有序列表"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/>
                  </svg>
                </button>
                <button
                  onMouseDown={preventScrollOnClick}
                  onClick={() => editor.chain().focus().toggleCodeBlock().run()}
                  className={`p-1.5 rounded transition-all ${
                    editor.isActive('codeBlock') 
                      ? 'bg-blue-500 text-white shadow-sm' 
                      : 'hover:bg-gray-200 text-gray-700'
                  }`}
                  title="代码块"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"/>
                  </svg>
                </button>
                <button
                  onMouseDown={preventScrollOnClick}
                  onClick={() => editor.chain().focus().toggleBlockquote().run()}
                  className={`p-1.5 rounded transition-all ${
                    editor.isActive('blockquote') 
                      ? 'bg-blue-500 text-white shadow-sm' 
                      : 'hover:bg-gray-200 text-gray-700'
                  }`}
                  title="引用"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
                  </svg>
                </button>
              </div>

              <div className="w-px h-6 bg-gray-300" />

              {/* 对齐组 */}
              <div className="flex items-center gap-0.5 px-1.5 py-1 bg-gray-50 rounded-md border border-gray-200">
                <button
                  onMouseDown={preventScrollOnClick}
                  onClick={() => editor.chain().focus().setTextAlign('left').run()}
                  className={`p-1.5 rounded transition-all ${
                    editor.isActive({ textAlign: 'left' }) 
                      ? 'bg-blue-500 text-white shadow-sm' 
                      : 'hover:bg-gray-200 text-gray-700'
                  }`}
                  title="居左对齐"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h10M4 18h16"/>
                  </svg>
                </button>
                <button
                  onMouseDown={preventScrollOnClick}
                  onClick={() => editor.chain().focus().setTextAlign('center').run()}
                  className={`p-1.5 rounded transition-all ${
                    editor.isActive({ textAlign: 'center' }) 
                      ? 'bg-blue-500 text-white shadow-sm' 
                      : 'hover:bg-gray-200 text-gray-700'
                  }`}
                  title="居中对齐"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M7 12h10M4 18h16"/>
                  </svg>
                </button>
                <button
                  onMouseDown={preventScrollOnClick}
                  onClick={() => editor.chain().focus().setTextAlign('right').run()}
                  className={`p-1.5 rounded transition-all ${
                    editor.isActive({ textAlign: 'right' }) 
                      ? 'bg-blue-500 text-white shadow-sm' 
                      : 'hover:bg-gray-200 text-gray-700'
                  }`}
                  title="居右对齐"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M10 12h10M4 18h16"/>
                  </svg>
                </button>
              </div>

              <div className="w-px h-6 bg-gray-300" />

            </div>

            {/* 右侧：全屏和退出 */}
            <div className="flex items-center gap-2">
              <button
                onMouseDown={preventScrollOnClick}
                onClick={() => setIsFullscreen(!isFullscreen)}
                className="p-1.5 px-2 text-xs rounded-md hover:bg-gray-100 border border-gray-200 text-gray-700 transition-all"
                title={isFullscreen ? '退出全屏' : '全屏编辑'}
              >
                {isFullscreen ? '退出全屏' : '全屏'}
              </button>
              {onExit && (
                <button
                  onClick={onExit}
                  className="p-1.5 px-3 text-xs rounded-md bg-blue-500 hover:bg-blue-600 text-white transition-all"
                >
                  保存退出
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 编辑器内容区域 */}
      <div className={`
        editor-wrapper
        ${isFullscreen ? 'flex-1 overflow-y-auto' : ''}
        ${editable ? 'bg-gray-50 py-6' : ''}
      `}>
        <div 
          className="editor-container"
          style={{
            // Word A4 页面：21cm ≈ 794px，减去左右边距 2.54cm (约96px) = 602px 内容宽度
            maxWidth: editable ? '794px' : '680px',  // 编辑时完整 A4 宽度，预览时略窄（更舒适）
            width: '100%',
            margin: '0 auto',  // 居中显示
            padding: editable ? '2.54cm' : '2rem 3rem',  // 编辑时标准边距，预览时左右留白
            outline: 'none',
            minHeight: editable ? '29.7cm' : 'auto',
            background: editable ? 'white' : 'transparent',
            boxShadow: editable ? '0 2px 8px rgba(0, 0, 0, 0.1)' : 'none',
          }}
        >
          <EditorContent editor={editor} />
        </div>
      </div>

      {/* 全局样式 */}
      <style>{`
        .editor-container .ProseMirror {
          outline: none;
          width: 100%;
          font-family: "Microsoft YaHei", "微软雅黑", sans-serif;
          font-size: 12pt;
        }
        .editor-container .ProseMirror p {
          margin-bottom: 0.75rem;
          line-height: 1.75;
          color: rgb(17, 24, 39);
          font-family: "Microsoft YaHei", "微软雅黑", sans-serif;
          font-size: 12pt;
        }
        .editor-container .ProseMirror h1 {
          font-size: 16pt;
          font-weight: 700;
          margin-bottom: 1rem;
          color: rgb(17, 24, 39);
          font-family: "Microsoft YaHei", "微软雅黑", sans-serif;
        }
        .editor-container .ProseMirror h2 {
          font-size: 14pt;
          font-weight: 600;
          margin-bottom: 0.75rem;
          color: rgb(17, 24, 39);
          font-family: "Microsoft YaHei", "微软雅黑", sans-serif;
        }
        .editor-container .ProseMirror h3 {
          font-size: 12pt;
          font-weight: 500;
          margin-bottom: 0.5rem;
          color: rgb(17, 24, 39);
          font-family: "Microsoft YaHei", "微软雅黑", sans-serif;
        }
        .editor-container .ProseMirror strong {
          font-weight: 600;
          color: rgb(17, 24, 39);
          font-family: "Microsoft YaHei", "微软雅黑", sans-serif;
        }
        .editor-container .ProseMirror em {
          font-style: italic;
          color: rgb(31, 41, 55);
          font-family: "Microsoft YaHei", "微软雅黑", sans-serif;
        }
        .editor-container .ProseMirror ul,
        .editor-container .ProseMirror ol {
          margin-bottom: 1rem;
          margin-left: 1.5rem;
          color: rgb(17, 24, 39);
          font-family: "Microsoft YaHei", "微软雅黑", sans-serif;
          font-size: 12pt;
        }
        .editor-container .ProseMirror ul {
          list-style-type: disc;
        }
        .editor-container .ProseMirror ol {
          list-style-type: decimal;
        }
        .editor-container .ProseMirror li {
          margin-bottom: 0.25rem;
          font-family: "Microsoft YaHei", "微软雅黑", sans-serif;
          font-size: 12pt;
        }
        .editor-container .ProseMirror code {
          padding: 0.125rem 0.25rem;
          background-color: rgb(243, 244, 246);
          border-radius: 0.25rem;
          font-size: 0.875rem;
          font-family: monospace;
          color: rgb(17, 24, 39);
        }
        .editor-container .ProseMirror pre {
          padding: 0.75rem;
          background-color: rgb(249, 250, 251);
          border-radius: 0.25rem;
          overflow-x: auto;
          font-size: 0.875rem;
          color: rgb(17, 24, 39);
        }
        .editor-container .ProseMirror blockquote {
          border-left: 4px solid rgb(229, 231, 235);
          padding-left: 1rem;
          margin-bottom: 1rem;
          color: rgb(75, 85, 99);
        }
        /* 表格样式 */
        .editor-container .ProseMirror table {
          border-collapse: collapse;
          margin-bottom: 1rem;
          width: 100%;
          table-layout: auto; /* 自动调整列宽 */
        }
        .editor-container .ProseMirror table td,
        .editor-container .ProseMirror table th {
          border: 1px solid rgb(209, 213, 219);
          padding: 0.5rem 1rem;
          font-family: "Microsoft YaHei", "微软雅黑", sans-serif;
          font-size: 12pt;
        }
        .editor-container .ProseMirror table th {
          font-weight: 600;
        }
        /* 确保表头内的所有元素继承文字颜色 */
        .editor-container .ProseMirror table th *,
        .editor-container .ProseMirror table th p {
          color: inherit !important;
        }
        .editor-container .ProseMirror img {
          max-width: 100%;
          height: auto;
          border-radius: 0.5rem;
          margin: 1rem 0;
        }
        /* 分页符样式 */
        .editor-container .page-break-node {
          margin: 1.5rem 0;
          padding: 0;
          user-select: none;
        }
        .editor-container .page-break-line {
          border-top: 2px dashed #9ca3af;
          position: relative;
          text-align: center;
          margin: 0.5rem 0;
        }
        .editor-container .page-break-text {
          position: relative;
          top: -0.75rem;
          padding: 0 0.5rem;
          color: #6b7280;
          font-size: 11px;
          font-weight: 500;
          letter-spacing: 0.05em;
        }
        /* 编辑模式下的分页符背景 */
        .unified-editor .editor-container .page-break-text {
          background: white;
        }
      `}</style>

      {/* 底部提示 - 仅编辑模式显示 */}
      {editable && !isFullscreen && (
        <div className="px-4 py-2 text-xs text-gray-500 border-t border-gray-200 bg-gray-50">
          <span>编辑后自动保存 · 点击编辑器外部或“退出编辑”按钮退出</span>
        </div>
      )}
      
      {/* 插入图片对话框 */}
      <ImageDialog
        show={showImageDialog}
        onClose={() => setShowImageDialog(false)}
        onInsert={handleInsertImage}
        selectedText={selectedTextForImage}
        businessLicenseUrl={businessLicenseUrl}
      />
      
      {/* 右键菜单 */}
      {editable && (
        <EditorContextMenu
          show={showContextMenu}
          x={contextMenuPosition.x}
          y={contextMenuPosition.y}
          onClose={() => setShowContextMenu(false)}
          onInsertMermaid={handleOpenMermaidDialog}
          onInsertTable={handleOpenTableDialog}
          onInsertImage={openImageDialog}
          onInsertPageBreak={handleInsertPageBreak}
          onDeletePageBreak={handleDeletePageBreak}
          onDeleteTable={handleDeleteTable}
          isInTable={isInTable}
          isInPageBreak={isInPageBreak}
          hasSelectedText={hasSelectedText}
          onOptimizeParagraph={handleOpenParagraphOptimize}
        />
      )}

      {/* Mermaid 对话框 */}
      {editable && (
        <MermaidDialog
          show={showMermaidDialog}
          onClose={() => {
            setShowMermaidDialog(false);
            setContextText('');
          }}
          onInsert={handleInsertMermaid}
          contextText={contextText}
          selectedModel={selectedModel}
        />
      )}

      {/* Table 对话框 */}
      {editable && (
        <TableDialog
          show={showTableDialog}
          onClose={() => {
            setShowTableDialog(false);
            setContextText('');
          }}
          onInsert={handleInsertTable}
          contextText={contextText}
          selectedModel={selectedModel}
        />
      )}

      {/* 段落优化对话框 */}
      {editable && selectedModel && (
        <ParagraphOptimizeDialog
          show={showParagraphOptimizeDialog}
          onClose={() => setShowParagraphOptimizeDialog(false)}
          onReplace={handleReplaceParagraph}
          selectedText={selectedTextForOptimize}
          selectedModel={selectedModel}
          chapterTitle="" // TODO: 从父组件传递章节名称
          chapterDescription="" // TODO: 从父组件传递章节描述
          projectContext={{
            projectName: '', // TODO: 从父组件传递
            companyName: '',
            projectOverview: '',
          }}
        />
      )}
    </div>
  );
};

export default UnifiedEditor;
